<template>
	<div v-if="hasBridgeIntegration" class="select-none">
		<div v-if="!compactMode" class="flex flex-col gap-0.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
			<div class="flex items-center gap-1.5">
				<span
					class="h-2 w-2 shrink-0 rounded-full"
					:class="isConnectedToBridge ? 'bg-green-500' : 'bg-red-500'"
				/>
				<span class="font-medium text-2xs" :class="isConnectedToBridge ? 'text-gray-600' : 'text-red-600'">
					{{ isConnectedToBridge ? $t("bridge.connected") : $t("bridge.disconnected") }}
				</span>
				<span v-if="currentVersion" class="ml-auto text-gray-400 text-2xs">v{{ currentVersion }}</span>
			</div>
			<a
				v-if="!isConnectedToBridge && !updating"
				href="pulsefood:start"
				class="mt-0.5 inline-flex items-center gap-1 self-start font-semibold text-red-600 text-2xs hover:underline"
			>
				<AppIcon icon="arrows-rotate" />
				<span>{{ $t("bridge.reconnect") }}</span>
			</a>
			<button
				v-if="latestVersion != currentVersion"
				type="button"
				:disabled="updating"
				@click="onUpdate"
				class="mt-0.5 inline-flex items-center gap-1 self-start font-semibold text-info text-2xs hover:underline disabled:cursor-wait disabled:no-underline disabled:opacity-70"
			>
				<AppIcon :icon="updating ? 'spinner' : 'cloud-arrow-down'" :spin-pulse="updating" />
				<span>{{ updating ? $t("bridge.updating") : $t("bridge.update", [latestVersion]) }}</span>
			</button>
		</div>
		<div v-else class="flex flex-col items-center py-1">
			<button
				v-if="latestVersion != currentVersion"
				type="button"
				:disabled="updating"
				@click="onUpdate"
				v-tooltip="{
					content: updating ? $t('bridge.updating') : $t('bridge.update', [latestVersion]),
				}"
				class="flex h-5 w-5 items-center justify-center rounded text-2xs text-info transition-colors hover:bg-primary-50 disabled:cursor-wait disabled:opacity-70"
			>
				<AppIcon :icon="updating ? 'spinner' : 'cloud-arrow-down'" :spin-pulse="updating" />
			</button>
			<a
				v-if="!isConnectedToBridge && !updating"
				href="pulsefood:start"
				v-tooltip="$t('bridge.reconnect')"
				class="flex h-5 w-5 items-center justify-center rounded text-2xs text-red-500 transition-colors hover:bg-red-50"
			>
				<AppIcon icon="arrows-rotate" />
			</a>
			<span
				v-else-if="isConnectedToBridge"
				v-tooltip="$t('bridge.connected-version', [currentVersion])"
				class="h-2 w-2 rounded-full bg-green-500"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { translate } from "~/plugins/I18n.ts";
import {
	hasBridgeIntegration,
	isConnectedToBridge,
	currentVersion,
	latestVersion,
	downloadUrl,
	updateBridgeIntegration,
} from "~utils/BridgeIntegration.ts";

defineProps<{ compactMode: boolean }>();

const { success, error } = useToast();

const updating = ref(false);

async function onUpdate() {
	if (updating.value || !downloadUrl.value) return;
	updating.value = true;
	try {
		const newVersion = await updateBridgeIntegration(downloadUrl.value);
		success(translate("bridge.update-success", [newVersion]));
	} catch (e) {
		console.warn("Bridge update failed:", e);
		error(translate("bridge.update-failed"));
	} finally {
		updating.value = false;
	}
}
</script>

<style scoped>
.text-2xs {
	font-size: 10px;
	line-height: 0.75rem;
}
</style>
